import { createRouter, createWebHistory } from 'vue-router';
import * as Sentry from '@sentry/vue';

import Auth from 'dashboard/api/auth';
import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { useTracking } from 'dashboard/composables/useTracking';
import { LAIS_TRACKING_CREATORS } from 'dashboard/helper/lais/trackingEvents';
import {
  clearRiskAssessmentAccountId,
  initRiskAssessmentForUser,
} from 'dashboard/helper/riskAssessment';
import {
  configureQAReferenceRouteAdapters,
  configureQAReferenceRouteFeatureFlagProvider,
  getQAReferenceIdentityWebappUrl,
  getQAReferenceRouteFeatureFlagEnabled,
  getQAReferenceRouteAdapters,
  QA_REFERENCE_ROUTE_FEATURE_FLAGS as FEATURE_FLAGS,
  QA_REFERENCE_ROUTE_PATHS as ROUTES,
} from 'dashboard/constants/lais/quintoAndarParity';

const getBrowserWindow = () => (typeof window === 'undefined' ? {} : window);

const requireIdentityWebappUrl = () => {
  const identityWebappUrl = getQAReferenceIdentityWebappUrl();

  if (!identityWebappUrl) {
    throw new Error('Missing identity webapp URL binding');
  }

  return identityWebappUrl;
};

const getReferenceLoginRedirect = () => {
  const identityWebappUrl = requireIdentityWebappUrl();

  const {
    pathname = '/',
    search = '',
    hash = '',
  } = getBrowserWindow().location || {};
  const redirectUrl = `${pathname}${search}${hash}`;

  return `${identityWebappUrl}/auth/login?redirect=${encodeURIComponent(redirectUrl)}`;
};

const getRuntimeReferenceFeatureFlagEnabled = featureFlag =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    store.getters.getCurrentAccountId,
    featureFlag
  );

const captureReferenceRouteMessage = error => Sentry.captureException(error);

configureQAReferenceRouteAdapters({
  hasAuthCookie: () => Auth.hasAuthCookie(),
  getLoginUrlWithRedirect: getReferenceLoginRedirect,
  getIdentityLogoutUrl: ({ loginUrlWithRedirect }) => {
    const identityWebappUrl = requireIdentityWebappUrl();

    return `${identityWebappUrl}/auth/logout?redirect=${encodeURIComponent(loginUrlWithRedirect)}`;
  },
  RiskAssessment: {
    clearAccountId: clearRiskAssessmentAccountId,
    setAccountId: () => initRiskAssessmentForUser(store.getters.getCurrentUser),
  },
  getFeatureFlagEnabled: getQAReferenceRouteFeatureFlagEnabled,
  captureMessage: captureReferenceRouteMessage,
});

const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

export const trackRoutePageView = (
  to,
  from = {},
  { tracking = useTracking() } = {}
) => {
  if (!to?.path) return null;
  if (from?.name === to.name && from?.path === to.path) return null;

  return tracking.track(LAIS_TRACKING_CREATORS.pageViewed, {
    variant: 'router_after_each',
    name: String(to.name || to.path),
    path: to.path,
    accountId: to.params?.accountId,
  });
};

export const validateAuthenticateRoutePermission = (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  if (to.name === 'no_accounts' || !to.name) {
    return next(frontendURL(`accounts/${accountId}/inicio`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const validateReferenceRoute = async (
  to,
  next,
  routeAdapters = getQAReferenceRouteAdapters()
) => {
  if (!to?.meta?.referenceAuthenticated) return false;

  const {
    hasAuthCookie,
    getLoginUrlWithRedirect,
    getIdentityLogoutUrl,
    RiskAssessment,
    getFeatureFlagEnabled,
    captureMessage,
  } = routeAdapters;

  if (!hasAuthCookie()) {
    RiskAssessment.clearAccountId();
    window.location.replace(
      getIdentityLogoutUrl({
        loginUrlWithRedirect: getLoginUrlWithRedirect(),
      })
    );
    return true;
  }

  if (to.meta.referenceRoot && !Object.keys(to.query || {}).length) {
    try {
      if (await getFeatureFlagEnabled(FEATURE_FLAGS.NEW_MENU_HOME_ITEM)) {
        window.location.href = ROUTES.HOME;
        return true;
      }
    } catch (error) {
      captureMessage(error);
    }
  }

  RiskAssessment.setAccountId();
  next();
  return true;
};

export const initalizeRouter = () => {
  let setUserPromise;

  configureQAReferenceRouteFeatureFlagProvider(
    getRuntimeReferenceFeatureFlagEnabled
  );

  const ensureCurrentUser = () => {
    if (!setUserPromise) {
      setUserPromise = store.dispatch('setUser');
    }

    return setUserPromise;
  };

  router.beforeEach(async (to, _from, next) => {
    if (await validateReferenceRoute(to, next)) return undefined;

    await ensureCurrentUser();
    validateAuthenticateRoutePermission(to, next, store);
    return undefined;
  });

  router.afterEach((to, from) => {
    trackRoutePageView(to, from);
  });
};

export default router;
